var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState, useCallback, useMemo, useRef } from 'react';
import { SelectInput } from '@ui/molecules/inputs/form/SelectInput';
import { Loader } from '@ui';
import { searchBuildings, searchAccounts } from '../services/TkbService';
/**
 * UX-rebuild scope picker (UX rebuild §Issues 4 & 5).
 *
 * Renders a searchable name-based picker appropriate for the current scope level:
 *   building      → async search endpoint (name + address, avoids loading all)
 *   account       → async search endpoint (name, avoids loading all)
 *   building_tag  → static SelectInput (tag vocabulary from bootstrap)
 *   tenant        → nothing (tenant-wide has no sub-key)
 *
 * No raw ID inputs anywhere user-facing.
 */
export default function ScopePicker({ scopeLevel, buildingId, customerAccountId, buildingTag, buildingTags, onBuildingChange, onAccountChange, onTagChange, t, disabled = false }) {
    var _a, _b, _c, _d;
    // Building search state
    const [buildingQuery, setBuildingQuery] = useState('');
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [buildingLoading, setBuildingLoading] = useState(false);
    // Track the current building display label so we can pre-fill it on edit.
    const [buildingLabel, setBuildingLabel] = useState('');
    // Account search state
    const [accountQuery, setAccountQuery] = useState('');
    const [accountOptions, setAccountOptions] = useState([]);
    const [accountLoading, setAccountLoading] = useState(false);
    const [accountLabel, setAccountLabel] = useState('');
    // Guards against stale async results: an older in-flight searchAccounts must
    // not overwrite the options/loading state of a newer query (fast typing).
    const latestAccountSearchId = useRef(0);
    const handleBuildingSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        setBuildingQuery(query);
        if (query.length < 2) {
            setBuildingOptions([]);
            return;
        }
        setBuildingLoading(true);
        try {
            const results = yield searchBuildings(query);
            setBuildingOptions(results);
        }
        catch (_a) {
            setBuildingOptions([]);
        }
        finally {
            setBuildingLoading(false);
        }
    }), []);
    const handleAccountSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        const searchId = ++latestAccountSearchId.current;
        setAccountQuery(query);
        if (query.length < 2) {
            setAccountOptions([]);
            setAccountLoading(false);
            return;
        }
        setAccountLoading(true);
        try {
            const results = yield searchAccounts(query);
            if (searchId !== latestAccountSearchId.current)
                return;
            setAccountOptions(results);
        }
        catch (_a) {
            if (searchId !== latestAccountSearchId.current)
                return;
            setAccountOptions([]);
        }
        finally {
            if (searchId === latestAccountSearchId.current) {
                setAccountLoading(false);
            }
        }
    }), []);
    const tagOptions = useMemo(() => buildingTags.map((tag) => ({ value: tag, label: tag })), [buildingTags]);
    const buildingSelectOptions = useMemo(() => buildingOptions.map((b) => ({ value: b.value, label: b.label })), [buildingOptions]);
    const accountSelectOptions = useMemo(() => accountOptions.map((a) => ({ value: a.value, label: a.label })), [accountOptions]);
    if (scopeLevel === 'tenant') {
        return null;
    }
    if (scopeLevel === 'buildings') {
        const selectedBuildingLabel = buildingId != null
            ? (_c = (_b = (_a = buildingOptions.find((b) => b.value === buildingId)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : buildingLabel) !== null && _c !== void 0 ? _c : String(buildingId)
            : '';
        return (React.createElement("div", { className: "tkb-scope-picker" },
            React.createElement("label", { className: "qmb-label", htmlFor: "tkb-scope-building-search" }, t('rules.form.building_label')),
            React.createElement("div", { className: "tkb-scope-picker__search-row" },
                React.createElement("input", { id: "tkb-scope-building-search", type: "text", className: "qmb-input--text", placeholder: t('rules.form.building_search_placeholder'), value: buildingQuery, onChange: (e) => {
                        void handleBuildingSearch(e.target.value);
                    }, disabled: disabled, autoComplete: "off" }),
                buildingLoading && React.createElement(Loader, { size: "16" })),
            buildingSelectOptions.length > 0 && (React.createElement(SelectInput, { id: "tkb-scope-building", label: t('rules.form.building_select_label'), options: buildingSelectOptions, value: buildingId !== null && buildingId !== void 0 ? buildingId : undefined, onChange: (val) => {
                    var _a, _b;
                    const opt = buildingOptions.find((b) => b.value === val);
                    setBuildingLabel((_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : '');
                    onBuildingChange(val, (_b = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _b !== void 0 ? _b : '');
                    setBuildingQuery('');
                    setBuildingOptions([]);
                }, disabled: disabled })),
            buildingId != null && (React.createElement("div", { className: "tkb-scope-picker__selected" },
                React.createElement("span", { className: "tkb-scope-picker__selected-label" }, selectedBuildingLabel),
                React.createElement("button", { type: "button", className: "qmb-control--icon", "aria-label": t('rules.form.clear_building'), onClick: () => {
                        onBuildingChange(null, '');
                        setBuildingLabel('');
                    }, disabled: disabled },
                    React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" }))))));
    }
    if (scopeLevel === 'accounts') {
        const selectedAccountLabel = customerAccountId != null
            ? ((_d = accountOptions.find((a) => a.value === customerAccountId)) === null || _d === void 0 ? void 0 : _d.label) || accountLabel || String(customerAccountId)
            : '';
        return (React.createElement("div", { className: "tkb-scope-picker" },
            React.createElement("label", { className: "qmb-label", htmlFor: "tkb-scope-account-search" }, t('rules.form.account_label')),
            React.createElement("div", { className: "tkb-scope-picker__search-row" },
                React.createElement("input", { id: "tkb-scope-account-search", type: "text", className: "qmb-input--text", "data-testid": "tkb-scope-account-search", placeholder: t('rules.form.account_search_placeholder'), value: accountQuery, onChange: (e) => {
                        void handleAccountSearch(e.target.value);
                    }, disabled: disabled, autoComplete: "off" }),
                accountLoading && React.createElement(Loader, { size: "16" })),
            accountSelectOptions.length > 0 && (React.createElement(SelectInput, { id: "tkb-scope-account", label: t('rules.form.account_select_label'), options: accountSelectOptions, value: customerAccountId !== null && customerAccountId !== void 0 ? customerAccountId : undefined, onChange: (val) => {
                    var _a;
                    const opt = accountOptions.find((a) => a.value === val);
                    setAccountLabel((_a = opt === null || opt === void 0 ? void 0 : opt.label) !== null && _a !== void 0 ? _a : '');
                    onAccountChange(val);
                    setAccountQuery('');
                    setAccountOptions([]);
                }, disabled: disabled })),
            customerAccountId != null && (React.createElement("div", { className: "tkb-scope-picker__selected" },
                React.createElement("span", { className: "tkb-scope-picker__selected-label" }, selectedAccountLabel),
                React.createElement("button", { type: "button", className: "qmb-control--icon", "aria-label": t('rules.form.clear_account'), onClick: () => {
                        onAccountChange(null);
                        setAccountLabel('');
                    }, disabled: disabled },
                    React.createElement("i", { className: "fa-light fa-xmark", "aria-hidden": "true" }))))));
    }
    if (scopeLevel === 'building_tag') {
        return (React.createElement("div", { className: "tkb-scope-picker" },
            React.createElement(SelectInput, { id: "tkb-scope-tag", label: t('rules.form.tag_label'), options: [{ value: '', label: t('rules.form.select_tag') }, ...tagOptions], value: buildingTag, onChange: (val) => onTagChange(val), showSearch: true, searchPlaceholder: t('rules.form.tag_search_placeholder'), disabled: disabled })));
    }
    return null;
}
